import { DeltaTrialFeature } from '../../Plan/DeltaTrial/DeltaTrialFeatures'
import { getUltimatePlanId } from '../../Plan/planConstants'
import { createAppUpsellBody } from '../../Plan/DeltaTrial/OAuth2App/OAuth2App'
import { createAppIntegrationTimeline } from '../../Plan/DeltaTrial/OAuth2App/OAuth2AppTimeline'
import { createAppIntegrationSuccess } from '../../Plan/DeltaTrial/OAuth2App/OAuth2AppSuccess'
import { OAuth2App, ShortOAuth2App } from '../../../store/auth/auth'
import { OAuth2AppUpsellFeatures } from '../../Plan/DeltaTrial/OAuth2App/OAuth2AppUpsellFeatures'
import { adminRoles, enterpriseAdminRoles, ownerRoles, professionalRoles, sidecarRoles, templateAdminRoles } from '../../ManageTeam/InviteRoleGroups'
import { env } from '../../../common/util'

const CALENDLY_URL = 'https://calendly.com/financial-advisor-zocks-discussion/ultimate-plan-demo-ia'

export type PermissionGroupKey = 'contacts' | 'meetings' | 'results' | 'other'

export const PERMISSION_GROUPS: Record<PermissionGroupKey, string[]> = {
  contacts: ['ext_user'],
  meetings: ['room_session'],
  results: ['ai_agent_result', 'airesult', 'ai_agent'],
  other: [],
}

export const PERMISSION_GROUP_LABELS: Record<PermissionGroupKey, string> = {
  contacts: 'Contacts',
  meetings: 'Meetings',
  results: 'Results',
  other: 'Other',
}

export const mapPermissionsByGroup = (permissions: { id: string; description: string }[]) => {
  return Object.entries(PERMISSION_GROUPS).reduce(
    (acc, [groupKey, prefixes]) => {
      acc[groupKey as PermissionGroupKey] = permissions.filter((permission) => {
        const prefix = permission.id.split(':')[0]
        if (groupKey === 'other') {
          return !Object.entries(PERMISSION_GROUPS).some(([key, groupPrefixes]) => key !== 'other' && groupPrefixes.includes(prefix))
        }
        return prefixes.includes(prefix)
      })
      return acc
    },
    {} as Record<PermissionGroupKey, { id: string; description: string }[]>,
  )
}

export const toBooleanSelectionMap = (allIds: string[], selectedIds: string[]) => {
  const selectedSet = new Set(selectedIds)
  return Object.fromEntries(allIds.map((id) => [id, selectedSet.has(id)]))
}

export const createDeltaTrialFeatureFromApp = (app: OAuth2App | ShortOAuth2App): DeltaTrialFeature => {
  return {
    id: `oauth2apps/${app.clientId}`,
    name: app.name,
    planId: getUltimatePlanId(),
    calendly: CALENDLY_URL,
    UpsellBody: createAppUpsellBody(app.name, app.imageUrl ?? ''),
    UpsellFeatures: OAuth2AppUpsellFeatures,
    DeltaTrialTimeline: createAppIntegrationTimeline(app.name),
    DeltaTrialSuccess: createAppIntegrationSuccess(app.name),
  }
}

export interface CombinedRoleGroup {
  name: string
  roleIds: string[]
}

export const ROLE_GROUP_NAMES = {
  ADMIN_OWNER_USERS: 'Account owners and admins',
  ALL_ACCOUNT_USERS: 'Everyone',
} as const

export const COMBINED_ROLE_GROUPS: CombinedRoleGroup[] = [
  {
    name: ROLE_GROUP_NAMES.ADMIN_OWNER_USERS,
    roleIds: [adminRoles[env], ownerRoles[env]],
  },
  {
    name: ROLE_GROUP_NAMES.ALL_ACCOUNT_USERS,
    roleIds: [adminRoles[env], ownerRoles[env], enterpriseAdminRoles[env], professionalRoles[env], sidecarRoles[env], templateAdminRoles[env]],
  },
]

export const getCoveredRoleGroups = (roleIds: string[]): CombinedRoleGroup[] => {
  const roleIdSet = new Set(roleIds)

  return COMBINED_ROLE_GROUPS.filter((roleGroup) => {
    if (roleGroup.roleIds.length !== roleIds.length) {
      return false
    }
    return roleGroup.roleIds.every((roleId) => roleIdSet.has(roleId))
  })
}
